// DLOM application — PSD §4.2.6 (F4.1, F4.2). Engine layer (framework-free).
import { bsPut } from './blackScholes';
import { DLOM_CLAMP_MAX, DLOM_CLAMP_MIN } from './constants';
/**
 * Apply a Discount for Lack of Marketability to common value.
 * Phase 1 ships "fixed" only; other models fall back with a warning.
 * Phase 2 adds real models (chaffe/bsPut/finnerty/longstaff) via dlomModels.ts.
 */
export function applyDlom(commonValuePre, D, A) {
    const issues = [];
    let pct = D.dlomPercentage;
    switch (D.dlomModel) {
        case 'fixed':
            pct = D.dlomPercentage;
            break;
        case 'chaffe':
        case 'bsPut':
            pct = bsPutImpliedDlom(A);
            break;
        case 'finnerty':
            pct = finnertyDlom(A);
            break;
        case 'longstaff':
            pct = longstaffLookbackDlom(A);
            break;
        default:
            issues.push({
                code: 'DLOM_MODEL_NOT_IMPLEMENTED',
                message: `DLOM model "${D.dlomModel}" not implemented; falling back to fixed percentage.`,
                instrumentId: null,
            });
            pct = D.dlomPercentage;
    }
    // Apply minority / lack-of-control discount first (Phase 2)
    const minority = Math.min(D.minorityDiscount, 1);
    let value = commonValuePre * (1 - Math.max(0, minority));
    // Clamp DLOM to [0, 0.60]
    if (pct < DLOM_CLAMP_MIN) {
        issues.push({
            code: 'NEGATIVE_DLOM_CLAMPED',
            message: `DLOM percentage ${pct} below 0 clamped to 0.`,
            instrumentId: null,
        });
        pct = DLOM_CLAMP_MIN;
    }
    else if (pct > DLOM_CLAMP_MAX) {
        issues.push({
            code: 'DLOM_CLAMPED',
            message: `DLOM percentage ${(pct * 100).toFixed(1)}% above 60% clamped to 60%.`,
            instrumentId: null,
        });
        pct = DLOM_CLAMP_MAX;
    }
    value = value * (1 - pct);
    return { value, percentage: pct, issues };
}
/** Chaffe / protective-put DLOM: ATM put price / spot. */
export function bsPutImpliedDlom(A) {
    const T = A.timeToLiquidity;
    const sigma = A.volatility;
    const r = A.riskFreeRate;
    const q = A.dividendYield;
    // at-the-money protective put: S = X = 1 (normalized)
    const put = bsPut(1, 1, T, r, sigma, q);
    return Math.min(DLOM_CLAMP_MAX, Math.max(DLOM_CLAMP_MIN, put));
}
/** Finnerty (2002) protective-put DLOM approximation. */
export function finnertyDlom(A) {
    const T = A.timeToLiquidity;
    const sigma = A.volatility;
    // Finnerty average-put approximation
    const pct = sigma * Math.sqrt(T) * 0.5;
    return Math.min(DLOM_CLAMP_MAX, Math.max(DLOM_CLAMP_MIN, pct));
}
/** Longstaff (1995) lookback-option upper bound DLOM. */
export function longstaffLookbackDlom(A) {
    const T = A.timeToLiquidity;
    const sigma = A.volatility;
    const r = A.riskFreeRate;
    const q = A.dividendYield;
    // Lookback call upper bound ≈ 2 * N(sigma*sqrt(T)/2) - 1 style; use put analogue.
    const put = bsPut(1, 1, T, r, sigma, q);
    const lookback = put * 2; // lookback >= vanilla
    return Math.min(DLOM_CLAMP_MAX, Math.max(DLOM_CLAMP_MIN, lookback));
}
//# sourceMappingURL=dlom.js.map